import { useEffect, useState } from 'react';
import './AreaSelector.scss';
import { SaudiArabiaAreas } from '../../../SourceData';
import BlueButton from '../../../components/BlueButton/BlueButton';
import { useMediaQuery } from '@mui/material';
import Loader from "../../../components/Loader";
import api from '../../../utils/api';
import { toast } from 'react-toastify';
import { usePermissions } from '../../../hooks/usePermissions';

export default function AreaSelector() {
  const [selectedGovernoratesIds, setSelectedGovernoratesIds] = useState([]);
  const [expandedAreas, setExpandedAreas] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeAreaIds, setActiveAreaIds] = useState([]); // Track areas with selected governorates
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");

  const isAreaFullySelected = (area) => {
    return area.governorates.every(gov => 
      selectedGovernoratesIds.includes(gov.id)
    );
  };

  const isAreaPartiallySelected = (area) => {
    const selectedCount = area.governorates.filter(gov => 
      selectedGovernoratesIds.includes(gov.id)
    ).length;
    return selectedCount > 0 && selectedCount < area.governorates.length;
  };

  // Check if area has any selected governorates (for green highlighting)
  const isAreaActive = (areaId) => {
    return activeAreaIds.includes(areaId);
  };

  const handleAreaToggle = (area) => {
    const allGovIds = area.governorates.map(gov => gov.id);
    
    if (isAreaFullySelected(area)) {
      setSelectedGovernoratesIds(prev => 
        prev.filter(id => !allGovIds.includes(id))
      );
    } else {
      setSelectedGovernoratesIds(prev => {
        const filtered = prev.filter(id => !allGovIds.includes(id));
        return [...filtered, ...allGovIds];
      });
    }
  };

  const handleGovernorateToggle = (govId) => {
    setSelectedGovernoratesIds(prev => {
      if (prev.includes(govId)) {
        return prev.filter(id => id !== govId);
      } else {
        return [...prev, govId];
      }
    });
  };

  const toggleAreaExpansion = (areaId) => {
    setExpandedAreas(prev => ({
      ...prev,
      [areaId]: !prev[areaId]
    }));
  };

  // Update active areas whenever selected governorates change
  useEffect(() => {
    const activeAreas = SaudiArabiaAreas.filter(area => 
      area.governorates.some(gov => selectedGovernoratesIds.includes(gov.id))
    ).map(area => area.areaId);
    
    setActiveAreaIds(activeAreas);
  }, [selectedGovernoratesIds]);

  useEffect(() => {
    fetchAreasAndGovernorates();
  }, []);

  const fetchAreasAndGovernorates = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/admin/locations");
      

      if (response.data.status === "success" && response.data.data) {
        // Extract all governorate IDs from the response
        const allSelectedGovIds = [];
        
        response.data.data.forEach(areaData => {
          if (areaData.governoratesIds && Array.isArray(areaData.governoratesIds)) {
            allSelectedGovIds.push(...areaData.governoratesIds);
          }
        });

        // Set the selected governorates
        setSelectedGovernoratesIds(allSelectedGovIds);
      }
    } catch (error) {
      console.error("Error fetching areas and governorates:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!hasPermission(133)) {
         toast.error('ليس لديك صلاحية لحفظ التغييرات');
    return;
    }

    try {
      setIsSubmitting(true);

      // Transform selected governorates into the required format
      const areasToSend = [];
      
      SaudiArabiaAreas.forEach(area => {
        const selectedGovs = area.governorates
          .filter(gov => selectedGovernoratesIds.includes(gov.id))
          .map(gov => gov.id);
        
        // Only add areas that have selected governorates (skip empty areas)
        if (selectedGovs.length > 0) {
          areasToSend.push({
            areaId: area.areaId,
            governoratesIds: selectedGovs
          });
        }
      });

      // Send to API with the new route and format
      const response = await api.post("/admin/areas-governorates", {
        areas: areasToSend
      });

 
      if (response.data.status === "success" && response.data.data) {
        // Extract all governorate IDs from the response
        const allSelectedGovIds = [];
        
        response.data.data.forEach(areaData => {
          if (areaData.governoratesIds && Array.isArray(areaData.governoratesIds)) {
            allSelectedGovIds.push(...areaData.governoratesIds);
          }
        });

        // Set the selected governorates
        setSelectedGovernoratesIds(allSelectedGovIds);
      }

 

      toast.success('تم حفظ التغييرات بنجاح');
    } catch (error) {
      console.error("Error saving locations:", error);
      toast.error('حدث خطأ أثناء حفظ التغييرات');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearAll = () => {
    setSelectedGovernoratesIds([]);
  };

  if (isLoading) {
    return (
      <Loader />
    );
  }

  return (
    <div className="area-selector container">
      <div className="header-section">
        <div className="title-group">
          <h1>اختيار المناطق والمحافظات</h1>
          <p>حدد المناطق والمحافظات التي سيعمل بها التطبيق</p>
        </div>
        
        <div className="stats-card">
          <div className="stat-item">
            <span className="stat-number">{selectedGovernoratesIds.length}</span>
            <span className="stat-label">محافظة محددة</span>
          </div>
        </div>
      </div>

      <div className="areas-grid">
        {SaudiArabiaAreas.map(area => {
          const isFullySelected = isAreaFullySelected(area);
          const isPartiallySelected = isAreaPartiallySelected(area);
          const isExpanded = expandedAreas[area.areaId];
          const isActive = isAreaActive(area.areaId);

          return (
            <div key={area.areaId} className={`area-box ${isExpanded ? 'expanded' : ''}`}>
              <div 
                className={`area-header ${isActive ? 'active' : ''}`} 
                onClick={() => toggleAreaExpansion(area.areaId)}
              >
                <div className="area-title-section">
                  <h2>{area.areaNameArabic}</h2>
                  <span className="gov-count">{area.governorates.length} محافظة</span>
                </div>
                
                <div className="area-actions">
                  <button 
                    className={`select-all-btn ${isFullySelected ? 'selected' : ''} ${isPartiallySelected ? 'partial' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAreaToggle(area);
                    }}
                  >
                    {isFullySelected ? 'إلغاء الكل' : 'اختيار الكل'}
                  </button>
                  
                  <div className={`expand-icon ${isExpanded ? 'rotated' : ''}`}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M15 8L10 13L5 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="governorates-grid">
                  {area.governorates.map(gov => {
                    const isSelected = selectedGovernoratesIds.includes(gov.id);
                    return (
                      <div 
                        key={gov.id} 
                        className={`gov-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleGovernorateToggle(gov.id)}
                      >
                        <div className="gov-content">
                          <span className="gov-name">{gov.nameArabic}</span>
                          <div className={`check-circle ${isSelected ? 'checked' : ''}`}>
                            {isSelected && (
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M3 8L6.5 11.5L13 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="footer-actions">
        <BlueButton
          text={isSubmitting ? "جاري الحفظ..." : "حفظ التغييرات"}
          variant="fill"
          size={isMobile ? "full" : "large"}
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="blue-button"
        />
      </div>
    </div>
  );
}